import { getTimestamp, addToCart } from 'ia-util-helpers'
import { EVENTS, subscribe } from 'ia-util-pubsub'

if (!customElements.get('ia-product-form')) {
  class IaProductForm extends HTMLElement {
    constructor () {
      super()
      this.section = this.closest('.shopify-section');
      this.form = this.querySelector('form')
      this.submitButton = this.querySelector('button[type="submit"]')
      this.submitButtonTextSpan = this.submitButton?.querySelector('span:not(.button__divider):not(.button__price-compare):not(.button__price)')
      this.textAddToCart = this.submitButton?.dataset.addToCartText || 'Add to Cart'
      this.textSoldOut = this.submitButton?.dataset.soldOutText || 'Sold Out'
      this.textUnavailable = this.submitButton?.dataset.unavailableText || 'Unavailable'
      this.cart = document.querySelector('ia-mini-cart')
      this.variantInput = this.querySelector('input[name="id"]')
      this.quantityInput = this.querySelector('ia-qty-picker')
      this.planInput = this.querySelector('input[name="selling_plan"]')
      window.iaTheme.extraOptions = window.iaTheme.extraOptions || new Map();
      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this))
        // Listen for extra option selection events and store in window.iaTheme.extraOptions
        subscribe(EVENTS.extraOptionSelected, (event) => {
          const { name, state } = event.detail || {};
          if (!name || !state) return;
          window.iaTheme.extraOptions.set(name, new Map(state));
          if (name === 'switch' && state.get('selectedVariantId')) {
            this.section.querySelector('ia-extra-option[data-name="switch"]')?.classList.remove('has-error');
          }
        });
      }
      
      this.unsubscribeVariantChange = subscribe(EVENTS.variantChange, this.handleVariantChange.bind(this))
      this.unsubscribeQtyChange = subscribe(EVENTS.qtyChange, this.handleQtyChange.bind(this))
      this.unsubscribePlanChange = subscribe(EVENTS.planChange, this.handlePlanChange.bind(this))
    }

    disconnectedCallback() {
      if (this.unsubscribeVariantChange) {
        this.unsubscribeVariantChange()
      }
      if (this.unsubscribeQtyChange) {
        this.unsubscribeQtyChange()
      }
      if (this.unsubscribePlanChange) {
        this.unsubscribePlanChange()
      }
    }

    get sectionId() {
      return this.getAttribute('section-id')
    }

    get productId() {
      return this.getAttribute('product-id')
    }

    handleVariantChange(event) {
      const { productId, sectionId, variant } = event.detail
      if (this.variantInput && productId == this.productId && sectionId == this.sectionId) {
        if (variant) {
          this.variantInput.value = variant.id
          if (this.submitButtonTextSpan) this.submitButtonTextSpan.textContent = variant.available ? this.textAddToCart : this.textSoldOut
          this.submitButton.toggleAttribute('disabled', !variant.available)
        } else {
          this.variantInput.value = ''
          if (this.submitButtonTextSpan) this.submitButtonTextSpan.textContent = this.textUnavailable
          this.submitButton.setAttribute('disabled', true)
        }
      }
    }

    handleQtyChange(event) {
      const { value, id } = event.detail
      if (this.quantityInput && this.quantityInput.dataset.id == id) {
        this.quantityInput.value = value
      }
    }

    async onSubmit (event) {
      event.preventDefault()
      if (!this.validateForm()) return false;

      if (this.submitButton.getAttribute('aria-disabled') === 'true') return

      this.handleErrorMessage()
      this.submitButton.setAttribute('aria-disabled', true)
      this.submitButton.classList.add('loading')

      try {
        const formData = new FormData(this.form)
        formData.append('sections', 'ia-mini-cart')
        this.cart?.preload()

        this.processExtraOptions(formData);

        const response = await addToCart(formData)
        this.handleSuccess(response)
      } catch (error) {
        this.handleError(error)
      } finally {
        this.submitButton.classList.remove('loading')
        this.submitButton.removeAttribute('aria-disabled')
      }
    }

    handleSuccess (data) {
      this.cart?.open(data)
    }

    handleError (error) {
      this.handleErrorMessage(error?.description || error.message || 'An error occurred')
    }

    handleErrorMessage (message = false) {
      const errorElement = this.querySelector('[data-error-message]')
      if (!errorElement) return

      errorElement.classList.toggle('hidden', !message)
      if (message) {
        errorElement.textContent = message
      }
    }

    handlePlanChange(event) {
      const { productId, sectionId, cadence, planId } = event.detail
      if (productId == this.productId && sectionId == this.sectionId) {
        console.log('form handlePlanChange', event);
        if (cadence == 'one-time') {
          this.planInput.value = ''
        }
        if (cadence == 'subscription') {
          this.planInput.value = planId
        }
      }
    }

    processExtraOptions(formData) {
      if (window.iaTheme.extraOptions.size === 0) return;
      const parentVariantId = this.variantInput.value;

      let quantity = 1;

      if (this.quantityInput && this.quantityInput.value) {
        quantity = parseInt(this.quantityInput.value, 10) || 1;
      } else {
        // Fallback
        const formQty = formData.get('quantity');
        if (formQty) {
          quantity = parseInt(formQty, 10) || 1;
        }
      }

      // Bulb: only add when a bulb is actually selected (removing selection leaves empty Map in extraOptions)
      const bulb = window.iaTheme.extraOptions.get('bulb');
      if (bulb && bulb.get('selectedVariantId')) {
        formData.append('items[Bulb Variant ID][id]', bulb.get('selectedVariantId'));
        formData.append('items[Bulb Variant ID][parent_id]', parentVariantId);
        formData.append('items[Bulb Variant ID][quantity]', quantity);
      }

      // Switch: only add when current variant requires it (Add Switch = Yes).
      // When switch is present, add unique _ts to main + switch so this set is a separate line item
      // and minicart can bulk-update qty (main + switch in sync). Bulb-only does not get _ts.
      const switchBlock = this.section?.querySelector('.ia-main-product__switch-type-selector');
      const variantIdsRequiringSwitch = (switchBlock?.dataset.variantsRequiringSwitch || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const currentVariantRequiresSwitch = variantIdsRequiringSwitch.includes(parentVariantId);

      const switchOption = window.iaTheme.extraOptions.get('switch');
      if (currentVariantRequiresSwitch && switchOption && switchOption.get('selectedVariantId')) {
        const lineSetTs = String(getTimestamp());
        formData.append('properties[_ts]', lineSetTs);
        formData.append('items[Switch Variant ID][id]', switchOption.get('selectedVariantId'));
        formData.append('items[Switch Variant ID][parent_id]', parentVariantId);
        formData.append('items[Switch Variant ID][quantity]', quantity);
        formData.append('items[Switch Variant ID][properties][_ia_switch_addon]', '1');
        formData.append('items[Switch Variant ID][properties][_ts]', lineSetTs);
        const switchProductId = switchOption.get('selectedProductId');
        if (switchProductId) {
          const nestedCable = window.iaTheme.extraOptions.get(`cable-color-${switchProductId}`);
          if (nestedCable && nestedCable.get('selectedTitle')) {
            formData.append('items[Switch Variant ID][properties][Cable Color]', nestedCable.get('selectedTitle'));
          }
        }
      }

      // Cable Color (main product)
      const cableColor = window.iaTheme.extraOptions.get('cable-color');
      if (cableColor) {
        formData.append('properties[Cable Color]', cableColor.get('selectedTitle'));
      }
    }

    validateForm() {
      // Switch (required when visible: current variant has "Add Switch" = Yes)
      const switchOptionTrigger = this.section.querySelector('ia-extra-option[data-name="switch"]');
      if (switchOptionTrigger) {
        const switchBlock = switchOptionTrigger.closest('.ia-main-product__switch-type-selector');
        const isSwitchVisible = switchBlock && switchBlock.getAttribute('aria-disabled') !== 'true';
        if (isSwitchVisible) {
          const switchOption = window.iaTheme.extraOptions.get('switch');
          if (switchOption && switchOption.get('selectedVariantId')) {
            switchOptionTrigger.classList.remove('has-error');
          } else {
            switchOptionTrigger.classList.add('has-error');
            return false;
          }
        }
      }

      // Cable Color (main product)
      const cableColorOption = this.section.querySelector('ia-extra-option[data-name="cable-color"]');
      if (!cableColorOption) {
        return true;
      }

      const cableColor = window.iaTheme.extraOptions.get('cable-color');
      if (cableColor && cableColor.get('selectedTitle')) {
        cableColorOption.classList.remove('has-error');
        return true;
      }
      cableColorOption.classList.add('has-error');
      return false;
    }
  }

  customElements.define('ia-product-form', IaProductForm)
}
